import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { AddCircle, Delete, Edit, History } from '@mui/icons-material';
import API from '../../services/api';
import DatePick from '../../components/DatePick';
import CustomSnackBar from '../../components/CustomSnackBar';
import EditPrice from './EditPrice';

const formatMoney = (value) =>
  new Intl.NumberFormat('es-GT', { style: 'currency', currency: 'GTQ' }).format(Number(value || 0));

const formatShortDate = (value) => {
  if (!value) return '-';
  const parsed = new Date(`${value}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? '-' : parsed.toLocaleDateString('es-GT');
};

const todayLocal = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const toLocalDate = (value) => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
    return todayLocal();
  }
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const emptyPriceForm = () => ({
  price: '',
  priceType: '',
  dateBegin: todayLocal(),
  comment: '',
});

export default function PriceManagerModal({ open, onClose, product, onRefresh }) {
  const [prices, setPrices] = useState([]);
  const [priceTypes, setPriceTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busyPriceId, setBusyPriceId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [newPrice, setNewPrice] = useState(emptyPriceForm);
  const [editPriceId, setEditPriceId] = useState(0);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState('');
  const [snackSeverity, setSnackSeverity] = useState('info');

  const showSnack = useCallback((message, severity = 'info') => {
    setSnackMessage(message);
    setSnackSeverity(severity);
    setSnackOpen(true);
  }, []);

  const fetchPrices = useCallback(async () => {
    if (!product?.id) {
      setPrices([]);
      return;
    }

    setLoading(true);
    try {
      const { data } = await API.get('price/', { params: { product: product.id } });
      const list = Array.isArray(data) ? data : data?.results || [];
      setPrices(list);
    } catch (error) {
      console.error('Error cargando precios del producto', error);
      setPrices([]);
      showSnack('No se pudieron cargar los precios', 'error');
    } finally {
      setLoading(false);
    }
  }, [product?.id, showSnack]);

  const fetchPriceTypes = useCallback(async () => {
    try {
      const { data } = await API.get('catalogs/price-types/');
      const list = Array.isArray(data) ? data : data?.results || [];
      setPriceTypes(list);
    } catch (error) {
      console.error('Error cargando tipos de precio', error);
      setPriceTypes([]);
    }
  }, []);

  useEffect(() => {
    if (!open || !product?.id) {
      return;
    }
    setShowForm(false);
    setNewPrice(emptyPriceForm());
    setEditPriceId(0);
    fetchPriceTypes();
    fetchPrices();
  }, [open, product?.id, fetchPriceTypes, fetchPrices]);

  const sortedPrices = useMemo(() => {
    const list = Array.isArray(prices) ? [...prices] : [];
    return list.sort((a, b) => {
      if (a.is_active !== b.is_active) {
        return a.is_active ? -1 : 1;
      }
      return new Date(b.dateBegin || 0) - new Date(a.dateBegin || 0);
    });
  }, [prices]);

  const activePrices = useMemo(
    () => sortedPrices.filter((price) => price.is_active === true),
    [sortedPrices]
  );

  const historicalPrices = useMemo(
    () => sortedPrices.filter((price) => price.is_active === false || Boolean(price.dateEnd)),
    [sortedPrices]
  );

  const supplierLabel = product?.supplier?.companyName || product?.supplier?.legalName || '-';

  const handleTogglePrice = async (price) => {
    if (!price?.id) return;
    setBusyPriceId(price.id);
    try {
      await API.patch(`price/${price.id}/`, {
        is_active: !price.is_active,
        dateEnd: price.is_active ? todayLocal() : null,
      });
      await fetchPrices();
      if (onRefresh) {
        await onRefresh();
      }
      showSnack(price.is_active ? 'Precio desactivado' : 'Precio activado', 'success');
    } catch (error) {
      console.error('Error actualizando precio', error);
      showSnack('No se pudo actualizar el precio', 'error');
    } finally {
      setBusyPriceId(null);
    }
  };

  const handleChangeNewPrice = (name, value) => {
    setNewPrice((prev) => ({ ...prev, [name]: value }));
  };

  const handleSavePrice = async () => {
    const parsedPrice = Number(newPrice.price);

    if (!parsedPrice || Number.isNaN(parsedPrice) || parsedPrice <= 0) {
      showSnack('Ingresa un precio válido', 'warning');
      return;
    }

    if (!newPrice.priceType) {
      showSnack('Selecciona un tipo de precio', 'warning');
      return;
    }

    if (!product?.id) {
      showSnack('No hay producto seleccionado', 'warning');
      return;
    }

    setSaving(true);
    try {
      const activeSameType = activePrices.find((item) => String(item?.priceType?.id || item?.priceType_id || '') === String(newPrice.priceType));
      if (activeSameType?.id) {
        await API.patch(`price/${activeSameType.id}/`, {
          is_active: false,
          dateEnd: newPrice.dateBegin || todayLocal(),
        });
      }
      await API.post('price/', {
        price: parsedPrice,
        priceType_id: Number(newPrice.priceType),
        dateBegin: newPrice.dateBegin,
        comment: newPrice.comment,
        product: product.id,
      });
      await fetchPrices();
      if (onRefresh) {
        await onRefresh();
      }
      setShowForm(false);
      setNewPrice(emptyPriceForm());
      showSnack('Precio creado correctamente', 'success');
    } catch (error) {
      console.error('Error creando precio', error);
      showSnack('No se pudo guardar el precio', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleEditSaved = useCallback(async () => {
    await fetchPrices();
    if (onRefresh) {
      await onRefresh();
    }
  }, [fetchPrices, onRefresh]);

  const tagSx = {
    fontSize: 11,
    px: '7px',
    py: '1px',
    borderRadius: '4px',
    border: '0.5px solid',
    borderColor: 'divider',
    backgroundColor: (theme) => theme.palette.background?.secondary || theme.palette.action.hover,
    color: 'text.secondary',
    lineHeight: 1.5,
    whiteSpace: 'nowrap',
  };

  const sectionLabelSx = {
    fontSize: 11,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    color: (theme) => theme.palette.text?.tertiary || theme.palette.text.disabled,
    mb: 1,
  };

  const cardSx = {
    border: '0.5px solid',
    borderColor: 'divider',
    borderRadius: '8px',
    px: '12px',
    py: '10px',
    mb: '6px',
  };

  return (
    <>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
          Gestión de precios
        </Typography>
        <Typography sx={{ mt: 0.5, fontSize: 12, color: 'text.secondary' }}>
          {product?.description || 'Producto'} · {product?.code || '-'} · {supplierLabel}
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ px: 2.5, py: 2 }}>
        {showForm && (
          <Box
            sx={{
              mb: 2,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1.25,
              alignItems: 'center',
              border: '0.5px solid',
              borderColor: 'divider',
              borderRadius: '8px',
              p: 1.5,
            }}
          >
            <TextField
              label="Precio"
              type="number"
              size="small"
              value={newPrice.price}
              onChange={(event) => handleChangeNewPrice('price', event.target.value)}
              sx={{ width: 120 }}
              inputProps={{ min: 0, step: '0.01' }}
            />

            <FormControl size="small" sx={{ minWidth: 180 }}>
              <Select
                displayEmpty
                value={newPrice.priceType}
                onChange={(event) => handleChangeNewPrice('priceType', event.target.value)}
              >
                <MenuItem value="">
                  <em>Tipo de precio</em>
                </MenuItem>
                {priceTypes.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <DatePick
              label="Desde"
              size="small"
              value={newPrice.dateBegin}
              onChange={(value) => handleChangeNewPrice('dateBegin', value ? toLocalDate(value) : todayLocal())}
              allowHistoricalDates
              allowFutureDates={false}
              maxWidth={170}
              minWidth={150}
            />

            <TextField
              label="Observacion"
              size="small"
              value={newPrice.comment}
              onChange={(event) => handleChangeNewPrice('comment', event.target.value)}
              sx={{ minWidth: 220, flex: 1 }}
            />

            <Button
              variant="contained"
              size="small"
              onClick={handleSavePrice}
              disabled={saving}
              sx={{ textTransform: 'none' }}
            >
              {saving ? 'Guardando...' : 'Guardar'}
            </Button>

            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setShowForm(false);
                setNewPrice(emptyPriceForm());
              }}
              disabled={saving}
              sx={{ textTransform: 'none' }}
            >
              Cancelar
            </Button>
          </Box>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
            <CircularProgress size={26} />
          </Box>
        ) : (
          <>
            <Typography sx={sectionLabelSx}>Precios activos</Typography>

            {!activePrices.length ? (
              <Box sx={cardSx}>
                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                  No hay precios activos registrados.
                </Typography>
              </Box>
            ) : (
              activePrices.map((price) => (
                <Box key={price.id} sx={cardSx}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontWeight: 500, fontSize: 14, minWidth: 75 }}>
                      {formatMoney(price.price)}
                    </Typography>

                    <Box component="span" sx={tagSx}>
                      {price.priceType?.name || 'Sin tipo'}
                    </Box>

                    <Chip size="small" color="success" label="Vigente" variant="outlined" />

                    <Typography sx={{ flex: 1, minWidth: 140, fontSize: 11, color: 'text.secondary' }}>
                      Desde {formatShortDate(price.dateBegin)}
                    </Typography>

                    <Button
                      size="small"
                      startIcon={<Edit fontSize="small" />}
                      onClick={() => setEditPriceId(price.id)}
                      sx={{ textTransform: 'none', fontSize: 12 }}
                    >
                      Editar
                    </Button>

                    <Button
                      size="small"
                      startIcon={<Delete fontSize="small" />}
                      onClick={() => handleTogglePrice(price)}
                      disabled={busyPriceId === price.id}
                      sx={{
                        textTransform: 'none',
                        fontSize: 12,
                        color: 'text.secondary',
                        '&:hover': {
                          color: 'error.main',
                          backgroundColor: (theme) => alpha(theme.palette.error.main, 0.08),
                        },
                      }}
                    >
                      Eliminar
                    </Button>
                  </Box>
                </Box>
              ))
            )}

            <Divider sx={{ my: '12px' }} />

            <Typography sx={sectionLabelSx}>Historial</Typography>

            {!historicalPrices.length ? (
              <Box
                sx={{
                  ...cardSx,
                  opacity: 0.55,
                  backgroundColor: (theme) => theme.palette.background?.secondary || theme.palette.action.hover,
                }}
              >
                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                  No hay registros históricos.
                </Typography>
              </Box>
            ) : (
              historicalPrices.map((price) => (
                <Box
                  key={`history-${price.id}`}
                  sx={{
                    ...cardSx,
                    opacity: 0.55,
                    backgroundColor: (theme) => theme.palette.background?.secondary || theme.palette.action.hover,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontWeight: 500, fontSize: 14, minWidth: 75 }}>
                      {formatMoney(price.price)}
                    </Typography>

                    <Box component="span" sx={tagSx}>
                      {price.priceType?.name || 'Sin tipo'}
                    </Box>

                    <Chip
                      size="small"
                      label={price.is_active === false ? 'Inactivo' : 'Historico'}
                      variant="outlined"
                    />

                    <Typography sx={{ flex: 1, minWidth: 170, fontSize: 11, color: 'text.secondary' }}>
                      Desde {formatShortDate(price.dateBegin)}
                      {price.dateEnd ? ` · Hasta ${formatShortDate(price.dateEnd)}` : ''}
                    </Typography>

                    <Button
                      size="small"
                      startIcon={<History fontSize="small" />}
                      sx={{ textTransform: 'none', fontSize: 12 }}
                    >
                      Ver
                    </Button>
                  </Box>
                </Box>
              ))
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5, justifyContent: 'space-between' }}>
        <Typography sx={{ fontSize: 12, color: (theme) => theme.palette.text?.tertiary || theme.palette.text.disabled }}>
          {historicalPrices.length} registros históricos
        </Typography>

        <Button
          variant="contained"
          startIcon={<AddCircle />}
          onClick={() => {
            setShowForm(true);
            setNewPrice(emptyPriceForm());
          }}
          sx={{
            textTransform: 'none',
            backgroundColor: '#10173c',
            '&:hover': {
              backgroundColor: '#10173c',
            },
          }}
        >
          Nuevo precio
        </Button>
      </DialogActions>

      <Dialog open={Boolean(editPriceId)} onClose={() => setEditPriceId(0)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: 14, fontWeight: 500 }}>
          Editar precio
        </DialogTitle>
        <DialogContent dividers>
          <EditPrice
            key={`${product?.id || 0}-${editPriceId}`}
            product={product?.id || 0}
            price={editPriceId || 0}
            setOpen={(value) => {
              if (!value) {
                setEditPriceId(0);
              }
            }}
            getDataProducts={handleEditSaved}
          />
        </DialogContent>
      </Dialog>

      <CustomSnackBar open={snackOpen} setOpen={setSnackOpen} message={snackMessage} severity={snackSeverity} />
    </>
  );
}

PriceManagerModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  product: PropTypes.object,
  onRefresh: PropTypes.func,
};
